/** How Megan may initiate contact. Inbound SMS / PSTN still work when configured. */

export const MEGAN_CONTACT_MODES = ["prefer_text", "prefer_call", "force_text", "force_call", "off"] as const;

export type MeganContactMode = (typeof MEGAN_CONTACT_MODES)[number];

export type MeganChannel = "text" | "call" | "none";

/** Duty Standby / Assist / overnight are normal (async). User-asked call_me is urgent. */
export type MeganUrgency = "normal" | "urgent";

export const DEFAULT_MEGAN_CONTACT_MODE: MeganContactMode = "prefer_text";

export const MEGAN_CONTACT_MODE_LABEL: Record<MeganContactMode, string> = {
  prefer_text: "Prefer text",
  prefer_call: "Prefer call",
  force_text: "Force text",
  force_call: "Force call",
  off: "Off",
};

export const MEGAN_CONTACT_MODE_HINT: Record<MeganContactMode, string> = {
  prefer_text: "Megan texts first. Duty Standby / Assist will not ring.",
  prefer_call: "Megan rings first. Texts if the phone path is down.",
  force_text: "Always SMS / RCS. Never ring.",
  force_call: "Always PSTN. Never text.",
  off: "No outbound Megan contact.",
};

export function isMeganContactMode(value: unknown): value is MeganContactMode {
  return typeof value === "string" && (MEGAN_CONTACT_MODES as readonly string[]).includes(value);
}

/**
 * Missing mode: Call my phone on → prefer_call (they opted into ringing).
 * Otherwise prefer_text (SMS is the async default).
 */
export function hydrateMeganContactMode(raw?: unknown, callPhone?: boolean): MeganContactMode {
  if (isMeganContactMode(raw)) return raw;
  return callPhone ? "prefer_call" : DEFAULT_MEGAN_CONTACT_MODE;
}

export function contactModeAllowsText(mode: MeganContactMode): boolean {
  return mode === "prefer_text" || mode === "force_text" || mode === "prefer_call";
}

export function contactModeAllowsCall(mode: MeganContactMode): boolean {
  return mode === "prefer_call" || mode === "force_call";
}

/** Keep Call my phone in sync so leftover PSTN gates stay honest. */
export function callPhoneForContactMode(mode: MeganContactMode): boolean {
  return contactModeAllowsCall(mode);
}

export function pickOutboundChannel(input: {
  mode: MeganContactMode;
  textReady: boolean;
  callReady: boolean;
  urgency?: MeganUrgency;
}): { channel: MeganChannel; reason: string } {
  const urgency = input.urgency ?? "normal";
  if (input.mode === "off") return { channel: "none", reason: "contact_off" };
  if (input.mode === "force_text") {
    return input.textReady
      ? { channel: "text", reason: "force_text" }
      : { channel: "none", reason: "force_text_unconfigured" };
  }
  if (input.mode === "force_call") {
    return input.callReady
      ? { channel: "call", reason: "force_call" }
      : { channel: "none", reason: "force_call_unconfigured" };
  }
  if (input.mode === "prefer_text") {
    if (input.textReady) return { channel: "text", reason: "prefer_text" };
    if (urgency === "urgent" && input.callReady) {
      return { channel: "call", reason: "prefer_text_urgent_fallback" };
    }
    return { channel: "none", reason: input.callReady ? "prefer_text_no_ring" : "prefer_text_unconfigured" };
  }
  if (input.callReady) return { channel: "call", reason: "prefer_call" };
  if (input.textReady) return { channel: "text", reason: "prefer_call_text_fallback" };
  return { channel: "none", reason: "prefer_call_unconfigured" };
}
